/**
 *
 * TCFCalendar
 *
 */

import React, { memo, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import moment from "utils/moment/momentLocale";
import { useTranslation } from 'react-i18next';

import en_US from 'antd/es/locale/en_US';
import fr_FR from 'antd/es/locale/fr_FR';
import de_DE from 'antd/es/locale/de_DE';

import 'moment/dist/locale/en-gb'; //english
import 'moment/dist/locale/fr'; //french
import 'moment/dist/locale/de'; //german

// styles
import './style.scss';

// antd component
import { Calendar, Button  } from 'antd';

// layout components
import ErrorBoundary from 'engine/ErrorBoundary';

// icons
import { LeftOutlined, RightOutlined } from '@ant-design/icons';

// utils
import { isToday } from "utils/moment/moment.utils";

function TCFCalendar({
  // states
  valueState,
  fullscreen,
  showTodayButton,
  mode,
  // actions
  onPanelChange,
  onToggleFullView,
  onSelect,
  onToggleLeft,
  onToggleRight,
  // default props
  className,
  ...rest
}) {
  const childClassNames = classNames('tcf-calendar-wrapper', className);
  const { t, i18n } = useTranslation();
  const lang = localStorage.getItem('i18nextLng') || process.env.DEFAULT_LANGUAGE || "fr";

  const now = moment();

  const calendarHeaderRender = useCallback(
    ({ value, type, onChange, onTypeChange }) => {
      value.locale(i18n?.language || lang);
      /* const lang = value.lang();
      const months =  lang.months(); */

      const month = value.month();
      const isTodayDate = isToday(value);

      return (
        <>
          {!!showTodayButton && !isTodayDate?.isSame && (
            <>
              <Button
                className="tcf-calendar__btn-today"
                onClick={() => onChange(now)}
              >
                {t("absences.today")}
              </Button>
            </>
          )}
          <div className="tcf-calendar__header">
            <h4
              className={`tcf-calendar__title ${i18n?.language}`}
              onClick={!!onToggleFullView ? onToggleFullView : undefined}
            >
              {value.format("dddd DD")}{" "}
              <span className={`month-year ${i18n?.language}`}>
                {value.format("MMMM YYYY")}
              </span>
            </h4>

            <div
              className="tcf-calendar__arrow tcf-calendar__btn-prev"
              onClick={() => {
                const selectedMonth = month - 1;
                const newValue = value.clone();
                newValue.month(parseInt(selectedMonth, 10));
                onChange(newValue);
                !!onToggleLeft && onToggleLeft(newValue);
              }}
            >
              <LeftOutlined />
            </div>

            <div
              className="tcf-calendar__arrow tcf-calendar__btn-next"
              onClick={() => {
                const selectedMonth = month + 1;
                const newValue = value.clone();
                newValue.month(parseInt(selectedMonth, 10));
                onChange(newValue);
                !!onToggleRight && onToggleRight(newValue);
              }}
            >
              <RightOutlined />
            </div>
          </div>
        </>
      );
    },
    [i18n?.language],
  );

  const calendarHeaderDayRender = useCallback(
    ({ value, type, onChange, onTypeChange }) => {
      value.locale(i18n?.language || lang);
      /*  const current = value.clone();
    const localeData = value.localeData();
    const months = [];
    for (let i = 0; i < 12; i++) {
      current.month(i);
      months.push(localeData.monthsShort(current));
    } */

      const day = value.day();

      return (
        <div className="tcf-calendar__header">
          <h4
            className="tcf-calendar__title"
            onClick={!!onToggleFullView ? onToggleFullView : undefined}
          >
            {value.format("dddd DD MMMM YYYY")}
          </h4>

          <div
            className="tcf-calendar__arrow tcf-calendar__btn-prev"
            onClick={() => {
              const selectedDay = day - 1;
              const newValue = value.clone();
              newValue.day(parseInt(selectedDay, 10));
              onChange(newValue);
              !!onToggleLeft && onToggleLeft(newValue);
            }}
          >
            <LeftOutlined />
          </div>

          <div
            className="tcf-calendar__arrow tcf-calendar__btn-next"
            onClick={() => {
              const selectedDay = day + 1;
              const newValue = value.clone();
              newValue.day(parseInt(selectedDay, 10));
              onChange(newValue);
              !!onToggleRight && onToggleRight(newValue);
            }}
          >
            <RightOutlined />
          </div>
        </div>
      );
    },
    [i18n?.language],
  );

  const initCalendarOptions = {};
  {!!onPanelChange && (initCalendarOptions.onPanelChange = onPanelChange )}
  { !!onSelect && (initCalendarOptions.onSelect = onSelect) }
  
  return (
    <div className={childClassNames}>
      <ErrorBoundary>
        <Calendar
          className={`tcf-calendar ${mode}`}
          value={valueState}
          fullscreen={false}
          headerRender={
            mode == "month" ? calendarHeaderRender : calendarHeaderDayRender
          }
          mode="month"
          {...initCalendarOptions}
        />
      </ErrorBoundary>
    </div>
  );
}

TCFCalendar.defaultProps = {
  mode: "month",
  fullscreen: false,
  showTodayButton: false,
};
TCFCalendar.propTypes = {
  mode: PropTypes.oneOf(["month", "day"]),
};

export default memo(TCFCalendar);
